import { useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Searchable project picker — replaces a plain <Select> when the project
 * list is too long to scroll comfortably. Type a few letters to filter.
 *
 * Props:
 *  - projects: array of { id, name, address?, is_active? }
 *  - value: selected project_id or ''
 *  - onChange: (project_id) => void
 *  - placeholder: trigger label when nothing is selected
 *  - filterActive: whether to filter to only is_active === true (default true)
 *  - testId: data-testid prefix
 */
export function ProjectCombobox({
  projects = [],
  value = '',
  onChange,
  placeholder = 'Select project',
  filterActive = true,
  disabled = false,
  testId = 'project-combobox',
}) {
  const [open, setOpen] = useState(false);

  const visible = (filterActive ? projects.filter((p) => p.is_active !== false) : projects)
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const selected = projects.find((p) => p.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid={`${testId}-trigger`}
        >
          {selected ? (
            <div className="flex flex-col items-start text-left overflow-hidden">
              <span className="truncate font-medium">{selected.name}</span>
              {selected.address && (
                <span className="text-xs text-gray-500 truncate">
                  {selected.address}
                </span>
              )}
              {selected.client_name && (
                <span className="text-[10px] text-gray-400 truncate">
                  {selected.client_name}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] max-w-[420px]"
        align="start"
        data-testid={`${testId}-content`}
      >
        <Command>
          <CommandInput
            placeholder="Type project name…"
            data-testid={`${testId}-input`}
          />
          <CommandList>
            <CommandEmpty>No project found.</CommandEmpty>
            <CommandGroup>
              {visible.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`${project.name} ${project.address || ''} ${project.client_name || ''}`}
                  onSelect={() => {
                    onChange?.(project.id);
                    setOpen(false);
                  }}
                  data-testid={`${testId}-option-${project.id}`}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === project.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium">{project.name}</span>
                    {project.address && (
                      <span className="text-xs text-gray-500 truncate">
                        {project.address}
                      </span>
                    )}
                    {project.client_name && (
                      <span className="text-[10px] text-gray-400 truncate">
                        {project.client_name}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ProjectCombobox;
